import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import type { ApiResponse } from '../types';

interface ThemeCompatibility {
  compatible: boolean;
  themeName: string | null;
  themeId: number | null;
  reason?: string;
}

interface ThemeCompatibilityContextValue {
  compatible: boolean | null;
  themeName: string | null;
  loading: boolean;
  error: string | null;
}

const ThemeCompatibilityContext = createContext<ThemeCompatibilityContextValue>({
  compatible: null,
  themeName: null,
  loading: true,
  error: null,
});

export function ThemeCompatibilityProvider({ children }: { children: ReactNode }) {
  const authenticatedFetch = useAuthenticatedFetch();
  const [compatible, setCompatible] = useState<boolean | null>(null);
  const [themeName, setThemeName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCompatibility = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch('/api/theme/compatibility');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json: ApiResponse<ThemeCompatibility> = await response.json();

      if (json.success && json.data) {
        setCompatible(json.data.compatible);
        setThemeName(json.data.themeName);
      } else {
        throw new Error(json.error || 'Unknown API error');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to check theme compatibility';
      setError(message);
      // Fail open — don't block merchants on error
      setCompatible(true);
      console.error('Theme compatibility check error:', err);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchCompatibility();
  }, [fetchCompatibility]);

  return (
    <ThemeCompatibilityContext.Provider value={{ compatible, themeName, loading, error }}>
      {children}
    </ThemeCompatibilityContext.Provider>
  );
}

export function useThemeCompatibility() {
  return useContext(ThemeCompatibilityContext);
}
